import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import axios from 'axios'

// amplify
import { Auth, API, Storage } from 'aws-amplify'
import { listOpportunitiesByOrganization, getOpportunity, listVolunteerMatchRequestsByOpportunity, listRolesByOpportunity, getRole, getShift, getOpportunityTemplate } from './../../graphql/queries'
import { createOpportunity, updateOpportunity, createTimeCommitment, createShift, createRole, createApplication, updateRole, updateShift, updateApplication, deleteRole, deleteShift } from './../../graphql/mutations'

import { toast } from 'react-toastify';
import { Opportunity, OpportunityStatus, Role, ScreeningCallRequiredOptions, Shift, TimeFrame, OpportunityType, RoleRecurrence } from '../../../API'
import { setLoading } from './loading'
import zipcodes from 'zipcodes'

import { getDay } from 'date-fns'
import format from 'date-fns/format'

interface OpportunitiesState {
    live: any,
    drafts: any,
    archive: any,
    selectedOpportunity: Opportunity | null,
    selectedOpportunityImage: string | null,
    selectedOpportunityRoles: Role[] | null,
    selectedOpportunityRole: Role | null,
    selectedOpportunityAction: SelectedOpportunityActionType | null,
    selectedOpportunityMatchRequests: any
    selectedOpportunityActiveVolunteers: any
}

enum SelectedOpportunityActionType {
    view,
    update,
}

interface OpportunityDetails {
    theWhy: string,
    expectedImpact: string,
    whatWillHappen: string,
    volunteerGain: string,
    stringifiedCauses: string,
    stringifiedCausesJSON: string
}

interface OpportunityApplication {
    screeningCallRequired: ScreeningCallRequiredOptions,
    recruitmentDeadlineForVolunteers: Date | null,
    targetNumberOfVolunteerHours: number | null,
    minimumNumberOfVolunteersRequired: number | null
}

interface IUpdateOpportunity {
    title: string | null,
    overview: string | null,
    descriptionRTE: string | null,
    description: string | null,
    responsibilitiesRTE: string | null,
    responsibilities: string | null,
}

const initialState: OpportunitiesState = {
    live: null,
    drafts: null,
    archive: null,
    selectedOpportunity: null,
    selectedOpportunityImage: null,
    selectedOpportunityAction: null,
    selectedOpportunityRoles: null,
    selectedOpportunityRole: null,
    selectedOpportunityMatchRequests: null,
    selectedOpportunityActiveVolunteers: null
}

export const getDraftOpportunities: any = createAsyncThunk(
    'opportunities/getDraftOpportunities',
    async (props: any, { getState }) => {
        try {
            const { authAccount }: any = getState()

            const { data }: any = await API.graphql({
                query: listOpportunitiesByOrganization,
                variables: {
                    filter: {
                        status: {
                            eq: 'draft'
                        }
                    },
                    organizationID: authAccount.selectedStaffProfile.organizationID
                }
            })

            console.log('draft opportunities: ', data)

            return data.listOpportunitiesByOrganization.items
        } catch (err) {
            console.log(err)
        }
    }
)

export const getLiveOpportunities: any = createAsyncThunk(
    'opportunities/getLiveOpportunities',
    async (props: any, { getState }) => {
        try {

            const { authAccount }: any = getState()

            const { data }: any = await API.graphql({
                query: listOpportunitiesByOrganization,
                variables: {
                    filter: {
                        status: {
                            eq: 'active'
                        }
                    },
                    organizationID: authAccount.selectedStaffProfile.organizationID
                }
            })

            console.log('active opportunities: ', data)

            return data.listOpportunitiesByOrganization.items
        } catch (err) {
            console.log(err)
        }
    }
)

export const getOpportunityMatchRequests: any = createAsyncThunk(
    'opportunities/getOpportunityMatchRequests',
    async (props: any, { getState }) => {
        try {

            const { opportunities }: any = getState()

            const { data }: any = await API.graphql({
                query: listVolunteerMatchRequestsByOpportunity,
                variables: {
                    opportunityID: opportunities.selectedOpportunity.id,
                    filter: {
                        status: {
                            eq: 'submitted' || 'inReview',
                        }
                    },
                }
            })

            console.log('selected opportunity match requests: ', data)

            return data.listVolunteerMatchRequestsByOpportunity.items
        } catch (err) {
            console.log(err)
        }
    }
)

export const getOpportunityActiveVolunteers: any = createAsyncThunk(
    'opportunities/getOpportunityActiveVolunteers',
    async (props: any, { getState }) => {
        try {

            const { opportunities }: any = getState()

            const { data }: any = await API.graphql({
                query: listVolunteerMatchRequestsByOpportunity,
                variables: {
                    opportunityID: opportunities.selectedOpportunity.id,
                    filter: {
                        status: {
                            eq: 'positionAssigned'
                        }
                    },
                }
            })

            console.log('selected opportunity active volunteers: ', data)

            return data.listVolunteerMatchRequestsByOpportunity.items
        } catch (err) {
            console.log(err)
        }
    }
)

export const createOpportunityDraft: any = createAsyncThunk(
    'opportunities/createOpportunity',
    async (props: { title: string, opportunityLeadID: string, opportunityProgramID: string | null, opportunityType: OpportunityType | null }, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Creating your opportunity...'
            }))

            const { authAccount }: any = getState()

            const { data }: any = await API.graphql({
                query: createOpportunity,
                variables: {
                    input: {
                        organizationID: authAccount.selectedStaffProfile.organizationID,
                        title: props.title,
                        type: props.opportunityType,
                        opportunityLeadID: props.opportunityLeadID,
                        programID: props.opportunityProgramID ? props.opportunityProgramID : authAccount.selectedStaffProfile.organizationID,
                        status: 'draft'
                    }
                }
            })
            const opportunity = data.createOpportunity

            // dispatch(push(`/admin/opportunities/create/what?id=${opportunity.id}`))

            console.log('new opportunity: ', opportunity)

            toast.success('Opportunity created.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });

            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))

            return opportunity
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.error(err)
        }
    }
)

export const createOpportunityFromTemplate: any = createAsyncThunk(
    'opportunities/createOpportunityFromTemplate',
    async (props: { templateID: string }, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Creating your opportunity from template. This could take a moment...'
            }))

            const { authAccount }: any = getState()

            console.log('copying template: ', props.templateID)

            const { data }: any = await API.graphql({
                query: getOpportunityTemplate,
                variables: {
                    id: props.templateID
                }
            })

            console.log(data)

            if (data.getOpportunityTemplate) {
                const opportunityTemplate: Opportunity = JSON.parse(data.getOpportunityTemplate.templateJSON)

                const createOpportunityResponse: any = await API.graphql({
                    query: createOpportunity,
                    variables: {
                        input: {
                            organizationID: authAccount.selectedStaffProfile.organizationID,
                            title: opportunityTemplate.title,
                            type: opportunityTemplate.type,
                            opportunityLeadID: opportunityTemplate.opportunityLeadID,
                            programID: opportunityTemplate.programID ? opportunityTemplate.programID : authAccount.selectedStaffProfile.organizationID,
                            status: 'draft',
                            descriptionRTE: opportunityTemplate.descriptionRTE,
                            theWhy: opportunityTemplate.theWhy,
                            expectedImpact: opportunityTemplate.expectedImpact,
                            whatWillHappen: opportunityTemplate.whatWillHappen,
                            volunteerGain: opportunityTemplate.volunteerGain,
                            stringifiedCauses: opportunityTemplate.stringifiedCauses,
                            stringifiedCausesJSON: opportunityTemplate.stringifiedCausesJSON
                        }
                    }
                })

                console.log('create opportunity response: ', createOpportunityResponse)

                const opportunity: Opportunity = createOpportunityResponse.data.createOpportunity

                if (opportunityTemplate.roles && opportunityTemplate.roles.items.length > 0) {
                    await asyncForEach(opportunityTemplate.roles.items, async (role: Role) => {
                        const createRoleResponse: any = await API.graphql({
                            query: createRole,
                            variables: {
                                input: {
                                    opportunityID: opportunity.id,
                                    organizationID: opportunity.organizationID,
                                    roleTitle: role.roleTitle,
                                    live: false,
                                    roleCoordinatorID: role.roleCoordinatorID,
                                    recurrence: role.recurrence,
                                    weeklyRecurrenceDays: role.weeklyRecurrenceDays,
                                    weeklyRecurrenceSameVolunteers: role.weeklyRecurrenceSameVolunteers,
                                    volunteersPerShift: role.volunteersPerShift,
                                    differentNumberOfVolunteersPerShift: role.differentNumberOfVolunteersPerShift,
                                    volunteerCommitmentMonths: role.volunteerCommitmentMonths,
                                    volunteerCommitmentCanRenew: role.volunteerCommitmentCanRenew,
                                    roleRequirements: role.roleRequirements,
                                    ageRequirement: role.ageRequirement,
                                    minorsRequireSupervision: role.minorsRequireSupervision,
                                    stringifiedSkills: role.stringifiedSkills,
                                    stringifiedSkillsJSON: role.stringifiedSkillsJSON,
                                    stringifiedCertifications: role.stringifiedCertifications
                                }
                            }
                        })

                        console.log('created role: ', createRoleResponse)

                        if (role.shifts && role.shifts.items.length > 0) {
                            await asyncForEach(role.shifts.items, async (shift: Shift) => {
                                const createShiftResponse: any = await API.graphql({
                                    query: createShift,
                                    variables: {
                                        input: {
                                            type: shift.type,
                                            live: false,
                                            name: shift.name,
                                            summary: shift.summary,
                                            startDate: shift.startDate,
                                            startTime: shift.startTime,
                                            endDate: shift.endDate,
                                            endTime: shift.endTime,
                                            timezone: shift.timezone,
                                            timeFrame: shift.timeFrame,
                                            timeFrameString: shift.timeFrameString,
                                            recurrence: shift.recurrence,
                                            weeklyRecurringDay: shift.weeklyRecurringDay,
                                            monthlyRecurrenceWeek: shift.monthlyRecurrenceWeek,
                                            monthlyRecurrenceDay: shift.monthlyRecurrenceDay,
                                            flexibleHours: shift.flexibleHours,
                                            flexibleTimePeriod: shift.flexibleTimePeriod,
                                            volunteersNeeded: shift.volunteersNeeded,
                                            volunteerCoordinatorID: shift.volunteerCoordinatorID,
                                            roleID: createRoleResponse.data.createRole.id,
                                            roleStringifiedSkills: shift.roleStringifiedSkills,
                                            roleStringifiedCauses: shift.roleStringifiedCauses,
                                            locationID: shift.locationID ? shift.locationID : '00000000-0000-0000-0000-000000000000',
                                            locationLongitude: shift.locationLongitude,
                                            locationLatitude: shift.locationLatitude,
                                            locationType: shift.locationType,
                                            locationZipEstimate: shift.locationZipEstimate,
                                            locationVariousDescription: shift.locationVariousDescription

                                        }
                                    }
                                })

                                console.log('created shift: ', createShiftResponse)
                            })
                        }
                    })
                }

                toast.success('Opportunity created.', {
                    position: "bottom-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    containerId: 'main'
                });

                dispatch(setLoading({
                    loading: false,
                    message: 'Loading'
                }))

                dispatch(setSelectedOpportunity(opportunity))

            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.error(err)
        }
    }
)

export const updateOpportunityBasics: any = createAsyncThunk(
    'opportunities/updateOpportunityBasics',
    async (props: { title: string, opportunityLeadID: string, opportunityProgramID: string | null, opportunityType: OpportunityType | null }, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Updating your opportunity...'
            }))

            const { opportunities, authAccount }: any = getState()

            const { data }: any = await API.graphql({
                query: updateOpportunity,
                variables: {
                    input: {
                        id: opportunities.selectedOpportunity.id,
                        title: props.title,
                        type: props.opportunityType,
                        opportunityLeadID: props.opportunityLeadID,
                        programID: props.opportunityProgramID ? props.opportunityProgramID : authAccount.selectedStaffProfile.organizationID
                    }
                }
            })

            console.log(data)

            const updatedOpportunity = data.updateOpportunity

            toast.success('Opportunity updated.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });

            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))

            return updatedOpportunity

        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.log('ERROR updating opportunity details.')
            console.log(err)
        }
    }
)

export const updateOpportunityDetails: any = createAsyncThunk(
    'opportunities/updateOpportunityDetails',
    async (props: OpportunityDetails, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Adding your opportunity details...'
            }))

            const { opportunities }: any = getState()

            const { data }: any = await API.graphql({
                query: updateOpportunity,
                variables: {
                    input: {
                        id: opportunities.selectedOpportunity.id,
                        theWhy: props.theWhy,
                        expectedImpact: props.expectedImpact,
                        whatWillHappen: props.whatWillHappen,
                        volunteerGain: props.volunteerGain,
                        stringifiedCauses: props.stringifiedCauses,
                        stringifiedCausesJSON: props.stringifiedCausesJSON
                    }
                }
            })

            console.log(data)

            const updatedOpportunity = data.updateOpportunity

            toast.success('Opportunity updated.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });

            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))

            return updatedOpportunity

        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.log('ERROR updating opportunity details.')
            console.log(err)
        }
    }
)

export const createNewRole: any = createAsyncThunk(
    'opportunities/createNewRole',
    async (props: any, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Creating a new role...'
            }))

            const { authAccount, opportunities }: any = getState()

            const createNewRoleResponse: any = await API.graphql({
                query: createRole,
                variables: {
                    input: {
                        opportunityID: opportunities.selectedOpportunity ? opportunities.selectedOpportunity.id : '00000000-0000-0000-0000-000000000000',
                        organizationID: authAccount.selectedStaffProfile ? authAccount.selectedStaffProfile.organizationID : '00000000-0000-0000-0000-000000000000',
                        roleTitle: 'New Role'
                    }
                }
            })

            if (createNewRoleResponse.data && createNewRoleResponse.data.createRole) {

                console.log('created role: ', createNewRoleResponse.data.createRole)

                toast.success('New role created.', {
                    position: "bottom-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    containerId: 'main'
                });

                dispatch(setLoading({
                    loading: false,
                    message: 'Loading'
                }))

                return { newRole: createNewRoleResponse.data.createRole }
            } else {
                return { newRole: null }
            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))
            console.log('ERROR creating new role.')
            console.log(err)
        }
    }
)

export const createNewShift: any = createAsyncThunk(
    'opportunities/createNewShift',
    async (props: any, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Creating a new shift...'
            }))

            const { authAccount, opportunities }: any = getState()

            const createNewShiftResponse: any = await API.graphql({
                query: createShift,
                variables: {
                    input: {
                        type: 'fixed',
                        roleID: opportunities.selectedOpportunityRole ? opportunities.selectedOpportunityRole.id : '00000000-0000-0000-0000-000000000000',
                        locationID: '00000000-0000-0000-0000-000000000000',
                        live: false,
                        recurrence: opportunities.selectedOpportunity ?
                            opportunities.selectedOpportunity.type == OpportunityType.oneTime ? 'oneTime'
                                : opportunities.selectedOpportunity.type == OpportunityType.asNeeded ? 'asNeeded'
                                    : opportunities.selectedOpportunity.type == OpportunityType.recurring && opportunities.selectedOpportunityRole && opportunities.selectedOpportunityRole.recurrence == RoleRecurrence.weekly ? 'weekly'
                                        : opportunities.selectedOpportunity.type == OpportunityType.recurring && opportunities.selectedOpportunityRole && opportunities.selectedOpportunityRole.recurrence == RoleRecurrence.monthly ? 'monthly'
                                            : '' : ''
                    }
                }
            })

            if (createNewShiftResponse.data && createNewShiftResponse.data.createShift) {
                toast.success('New shift created.', {
                    position: "bottom-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    containerId: 'main'
                });

                dispatch(setLoading({
                    loading: false,
                    message: 'Loading'
                }))

                return { newShift: createNewShiftResponse.data.createShift }
            } else {
                return { newShift: null }
            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))
            console.log('ERROR creating new role.')
            console.log(err)
        }
    }
)

export const removeRole: any = createAsyncThunk(
    'opportunities/removeRole',
    async (roleID: string, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Deleting role...'
            }))

            const { authAccount, opportunities }: any = getState()

            const getRoleResponse: any = await API.graphql({
                query: getRole,
                variables: {
                    id: roleID
                }
            })

            if (getRoleResponse && getRoleResponse.data && getRoleResponse.data.getRole) {

                const role = getRoleResponse.data.getRole

                const deleteRoleResponse: any = await API.graphql({
                    query: deleteRole,
                    variables: {
                        input: {
                            id: role.id,
                        }
                    }
                })

                if (role.shifts && role.shifts.items && role.shifts.items.length > 0) {
                    await asyncForEach(role.shifts.items, async (shift: Shift) => {
                        const deleteRoleShiftResponse = await API.graphql({
                            query: deleteShift,
                            variables: {
                                input: {
                                    id: shift.id,
                                }
                            }
                        })
                    })
                }

                if (deleteRoleResponse.data && deleteRoleResponse.data.deleteRole) {
                    console.log('removed role: ', deleteRoleResponse.data.deleteRole)
                    toast.success('Role deleted.', {
                        position: "bottom-right",
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        containerId: 'main'
                    });

                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading'
                    }))

                    return { removedRole: deleteRoleResponse.data.deleteRole }
                } else {
                    return { removedRole: null }
                }
            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))
            console.log('ERROR creating new role.')
            console.log(err)
        }
    }
)

export const removeShift: any = createAsyncThunk(
    'opportunities/removeRole',
    async (shiftID: string, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Deleting shift...'
            }))

            const deleteShiftResponse: any = await API.graphql({
                query: deleteShift,
                variables: {
                    input: {
                        id: shiftID
                    }
                }
            })



            if (deleteShiftResponse.data && deleteShiftResponse.data.deleteShift) {
                console.log('removed shift: ', deleteShiftResponse.data.deleteShift)
                toast.success('Shift deleted.', {
                    position: "bottom-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    containerId: 'main'
                });

                dispatch(setLoading({
                    loading: false,
                    message: 'Loading'
                }))

                return { removedShift: deleteShiftResponse.data.deleteShift }
            } else {
                return { removedShift: null }
            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))
            console.log('ERROR creating new role.')
            console.log(err)
        }
    }
)

export const duplicateRole: any = createAsyncThunk(
    'opportunities/duplicateRole',
    async (roleID: string, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Duplicating role...'
            }))

            const { authAccount, opportunities }: any = getState()

            const getRoleResponse: any = await API.graphql({
                query: getRole,
                variables: {
                    id: roleID
                }
            })

            if (getRoleResponse && getRoleResponse.data && getRoleResponse.data.getRole) {

                const role = getRoleResponse.data.getRole

                const createRoleResponse: any = await API.graphql({
                    query: createRole,
                    variables: {
                        input: {
                            opportunityID: role.opportunityID,
                            organizationID: role.organizationID,
                            roleTitle: role.roleTitle + ' Copy',
                            live: false,
                            roleCoordinatorID: role.roleCoordinatorID,
                            recurrence: role.recurrence,
                            weeklyRecurrenceDays: role.weeklyRecurrenceDays,
                            weeklyRecurrenceSameVolunteers: role.weeklyRecurrenceSameVolunteers,
                            volunteersPerShift: role.volunteersPerShift,
                            differentNumberOfVolunteersPerShift: role.differentNumberOfVolunteersPerShift,
                            volunteerCommitmentMonths: role.volunteerCommitmentMonths,
                            volunteerCommitmentCanRenew: role.volunteerCommitmentCanRenew,
                            roleRequirements: role.roleRequirements,
                            ageRequirement: role.ageRequirement,
                            minorsRequireSupervision: role.minorsRequireSupervision,
                            stringifiedSkills: role.stringifiedSkills,
                            stringifiedSkillsJSON: role.stringifiedSkillsJSON,
                            stringifiedCertifications: role.stringifiedCertifications
                        }
                    }
                })

                if (role.shifts && role.shifts.items && role.shifts.items.length > 0) {
                    await asyncForEach(role.shifts.items, async (shift: Shift) => {
                        const createRoleShift = await API.graphql({
                            query: createShift,
                            variables: {
                                input: {
                                    type: shift.type,
                                    live: false,
                                    name: shift.name,
                                    summary: shift.summary,
                                    startDate: shift.startDate,
                                    startTime: shift.startTime,
                                    endDate: shift.endDate,
                                    endTime: shift.endTime,
                                    timezone: shift.timezone,
                                    timeFrame: shift.timeFrame,
                                    timeFrameString: shift.timeFrameString,
                                    recurrence: shift.recurrence,
                                    weeklyRecurringDay: shift.weeklyRecurringDay,
                                    monthlyRecurrenceWeek: shift.monthlyRecurrenceWeek,
                                    monthlyRecurrenceDay: shift.monthlyRecurrenceDay,
                                    flexibleHours: shift.flexibleHours,
                                    flexibleTimePeriod: shift.flexibleTimePeriod,
                                    volunteersNeeded: shift.volunteersNeeded,
                                    volunteerCoordinatorID: shift.volunteerCoordinatorID,
                                    roleID: createRoleResponse.data.createRole.id,
                                    roleStringifiedSkills: shift.roleStringifiedSkills,
                                    roleStringifiedCauses: shift.roleStringifiedCauses,
                                    locationID: shift.locationID,
                                    locationLongitude: shift.locationLongitude,
                                    locationLatitude: shift.locationLatitude,
                                    locationType: shift.locationType,
                                    locationZipEstimate: shift.locationZipEstimate,
                                    locationVariousDescription: shift.locationVariousDescription
                                }
                            }
                        })
                    })
                }

                const getDuplicatedRoleResponse: any = await API.graphql({
                    query: getRole,
                    variables: {
                        id: createRoleResponse.data.createRole.id
                    }
                })

                if (getDuplicatedRoleResponse.data && getDuplicatedRoleResponse.data.getRole) {
                    console.log('duplicated role: ', getDuplicatedRoleResponse.data.getRole)
                    toast.success('Role duplicated.', {
                        position: "bottom-right",
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        containerId: 'main'
                    });

                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading'
                    }))

                    return { duplicatedRole: getDuplicatedRoleResponse.data.getRole }
                } else {
                    return { duplicatedRole: null }
                }
            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))
            console.log('ERROR creating new role.')
            console.log(err)
        }
    }
)

export const duplicateShift: any = createAsyncThunk(
    'opportunities/duplicateShift',
    async (shiftID: string, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Duplicating shift...'
            }))

            const { authAccount, opportunities }: any = getState()

            const getShiftResponse: any = await API.graphql({
                query: getShift,
                variables: {
                    id: shiftID
                }
            })

            console.log(getShiftResponse)

            if (getShiftResponse && getShiftResponse.data && getShiftResponse.data.getShift) {

                const shift = getShiftResponse.data.getShift

                console.log('now attempting to duplicate: ', shift)

                const createShiftResponse: any = await API.graphql({
                    query: createShift,
                    variables: {
                        input: {
                            type: shift.type,
                            live: shift.live,
                            name: shift.name,
                            summary: shift.summary,
                            startDate: shift.startDate,
                            startTime: shift.startTime,
                            endDate: shift.endDate,
                            endTime: shift.endTime,
                            timezone: shift.timezone,
                            timeFrame: shift.timeFrame,
                            timeFrameString: shift.timeFrameString,
                            recurrence: shift.recurrence,
                            weeklyRecurringDay: shift.weeklyRecurringDay,
                            monthlyRecurrenceWeek: shift.monthlyRecurrenceWeek,
                            monthlyRecurrenceDay: shift.monthlyRecurrenceDay,
                            flexibleHours: shift.flexibleHours,
                            flexibleTimePeriod: shift.flexibleTimePeriod,
                            volunteersNeeded: shift.volunteersNeeded,
                            volunteerCoordinatorID: shift.volunteerCoordinatorID,
                            roleID: shift.roleID,
                            roleStringifiedSkills: shift.roleStringifiedSkills,
                            roleStringifiedCauses: shift.roleStringifiedCauses,
                            locationID: shift.locationID,
                            locationLongitude: shift.locationLongitude,
                            locationLatitude: shift.locationLatitude,
                            locationType: shift.locationType,
                            locationZipEstimate: shift.locationZipEstimate,
                            locationVariousDescription: shift.locationVariousDescription,
                        }
                    }
                })

                if (createShiftResponse.data && createShiftResponse.data.createShift) {
                    console.log('duplicated shift: ', createShiftResponse.data.createShift)
                    toast.success('Shift duplicated.', {
                        position: "bottom-right",
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        containerId: 'main'
                    });

                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading'
                    }))

                    return { duplicatedShift: createShiftResponse.data.createShift }
                } else {
                    return { duplicatedShift: null }
                }
            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))
            console.log('ERROR creating new role.')
            console.log(err)
        }
    }
)

export const saveRole: any = createAsyncThunk(
    'opportunities/saveRole',
    async (props: { role: Role, deletedShifts?: Shift[] }, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Saving role. This may take a few moments...'
            }))

            const { authAccount, opportunities }: any = getState()

            var updatedRoles = []

            console.log(authAccount.selectedStaffProfile)
            console.log(opportunities.selectedOpportunity)

            var createOrUpdateRoleResponse: any = null

            const updateRoleResponse: any = await API.graphql({
                query: updateRole,
                variables: {
                    input: {
                        id: props.role.id,
                        opportunityID: props.role.opportunityID,
                        organizationID: props.role.organizationID,
                        roleTitle: props.role.roleTitle,
                        roleCoordinatorID: props.role.roleCoordinatorID,
                        roleRequirements: props.role.roleRequirements,
                        ageRequirement: props.role.ageRequirement ? props.role.ageRequirement : null,
                        minorsRequireSupervision: props.role.minorsRequireSupervision ? props.role.minorsRequireSupervision : null,
                        stringifiedSkills: props.role.stringifiedSkills,
                        stringifiedSkillsJSON: props.role.stringifiedSkillsJSON,
                        stringifiedCertifications: props.role.stringifiedCertifications,
                        recurrence: props.role.recurrence,
                        weeklyRecurrenceDays: props.role.weeklyRecurrenceDays,
                        weeklyRecurrenceSameVolunteers: props.role.weeklyRecurrenceSameVolunteers,
                        volunteersPerShift: props.role.volunteersPerShift,
                        differentNumberOfVolunteersPerShift: props.role.differentNumberOfVolunteersPerShift,
                        volunteerCommitmentMonths: props.role.volunteerCommitmentMonths,
                        volunteerCommitmentCanRenew: props.role.volunteerCommitmentCanRenew
                    }
                }
            })

            createOrUpdateRoleResponse = updateRoleResponse.data.updateRole
            console.log('updated roles: ', updateRoleResponse.data.updateRole)
            updatedRoles.push(updateRoleResponse.data.updateRole)

            if (props.role.shifts && props.role.shifts.items && props.role.shifts.items.length > 0) {

                var updatedShifts = []

                await asyncForEach(props.role.shifts.items, async (shift: Shift) => {
                    if (shift.id == '00000000-0000-0000-0000-000000000000') {

                        var locationLongitude = null;
                        var locationLatitude = null;

                        if (shift.locationZipEstimate) {
                            const zipcodeLookup = zipcodes.lookup(shift.locationZipEstimate)
                            locationLongitude = zipcodeLookup ? zipcodeLookup.longitude : null
                            locationLatitude = zipcodeLookup ? zipcodeLookup.latitude : null
                        } else {
                            locationLongitude = shift.locationLongitude ? shift.locationLongitude : shift.location && shift.location.address && shift.location.address.longitude ? shift.location.address.longitude : null
                            locationLatitude = shift.locationLatitude ? shift.locationLatitude : shift.location && shift.location.address && shift.location.address.latitude ? shift.location.address.latitude : null
                        }

                        const weeklyRecurringMultipleDays: any = null

                        const timeFrameString = shift.type == 'fixed' ? shift.startTime ? await calculateTimeFrame(shift.startTime, shift.recurrence && shift.recurrence == 'weekly' && shift.weeklyRecurringDay && shift.weeklyRecurringDay != 'multiple' ? shift.weeklyRecurringDay : shift.recurrence && shift.recurrence == 'weekly' && shift.weeklyRecurringDay && shift.weeklyRecurringDay == 'multiple' ? createOrUpdateRoleResponse.weeklyRecurrenceDays : shift.recurrence && shift.recurrence == 'monthly' && shift.monthlyRecurrenceDay ? shift.monthlyRecurrenceDay : shift.startDate ? shift.startDate : '') : null : 'flexible'

                        const createShiftResponse: any = await API.graphql({
                            query: createShift,
                            variables: {
                                input: {
                                    type: shift.type,
                                    name: shift.name ? shift.name : null,
                                    summary: shift.summary ? shift.summary : null,
                                    startDate: shift.startDate ? shift.startDate : null,
                                    startTime: shift.startTime ? shift.startTime : null,
                                    endDate: shift.endDate ? shift.endDate : null,
                                    endTime: shift.endTime ? shift.endTime : null,
                                    timeFrameString: timeFrameString,
                                    recurrence: shift.recurrence ? shift.recurrence : null,
                                    flexibleHours: shift.flexibleHours ? shift.flexibleHours : null,
                                    flexibleTimePeriod: shift.flexibleTimePeriod ? shift.flexibleTimePeriod : null,
                                    volunteersNeeded: createOrUpdateRoleResponse.differentNumberOfVolunteersPerShift ? shift.volunteersNeeded : createOrUpdateRoleResponse.volunteersPerShift,
                                    volunteerCoordinatorID: shift.volunteerCoordinatorID ? shift.volunteerCoordinatorID : props.role.roleCoordinatorID,
                                    roleID: createOrUpdateRoleResponse.id,
                                    roleStringifiedSkills: createOrUpdateRoleResponse.stringifiedSkills,
                                    roleStringifiedCauses: opportunities.selectedOpportunity.stringifiedCauses ? opportunities.selectedOpportunity.stringifiedCauses : '',
                                    locationID: shift.locationID ? shift.locationID : '00000000-0000-0000-0000-000000000000',
                                    locationLongitude: locationLongitude,
                                    locationLatitude: locationLatitude,
                                    locationZipEstimate: shift.locationZipEstimate ? shift.locationZipEstimate : null,
                                    locationType: shift.locationType ? shift.locationType : null,
                                    monthlyRecurrenceWeek: shift.monthlyRecurrenceWeek,
                                    monthlyRecurrenceDay: shift.monthlyRecurrenceDay,
                                    weeklyRecurringDay: shift.weeklyRecurringDay
                                }
                            }
                        })

                        console.log('created shift: ', createShiftResponse.data.createShift)
                        updatedShifts.push(createShiftResponse.data.createShift)
                    } else {

                        var locationLongitude = null;
                        var locationLatitude = null;

                        if (shift.locationZipEstimate) {
                            const zipcodeLookup = zipcodes.lookup(shift.locationZipEstimate)
                            locationLongitude = zipcodeLookup ? zipcodeLookup.longitude : null
                            locationLatitude = zipcodeLookup ? zipcodeLookup.latitude : null
                        } else {
                            locationLongitude = shift.locationLongitude ? shift.locationLongitude : shift.location && shift.location.address && shift.location.address.longitude ? shift.location.address.longitude : null
                            locationLatitude = shift.locationLatitude ? shift.locationLatitude : shift.location && shift.location.address && shift.location.address.latitude ? shift.location.address.latitude : null
                        }

                        const timeFrameString = shift.type == 'fixed' ? shift.startTime ? await calculateTimeFrame(shift.startTime, shift.recurrence && shift.recurrence == 'weekly' && shift.weeklyRecurringDay && shift.weeklyRecurringDay != 'multiple' ? shift.weeklyRecurringDay : shift.recurrence && shift.recurrence == 'weekly' && shift.weeklyRecurringDay && shift.weeklyRecurringDay == 'multiple' ? createOrUpdateRoleResponse.weeklyRecurrenceDays : shift.recurrence && shift.recurrence == 'monthly' && shift.monthlyRecurrenceDay ? shift.monthlyRecurrenceDay : shift.startDate ? shift.startDate : '') : null : 'flexible'

                        const updateShiftResponse: any = await API.graphql({
                            query: updateShift,
                            variables: {
                                input: {
                                    id: shift.id,
                                    type: shift.type,
                                    name: shift.name ? shift.name : null,
                                    summary: shift.summary ? shift.summary : null,
                                    startDate: shift.startDate ? shift.startDate : null,
                                    startTime: shift.startTime ? shift.startTime : null,
                                    endDate: shift.endDate ? shift.endDate : null,
                                    endTime: shift.endTime ? shift.endTime : null,
                                    timeFrameString: timeFrameString,
                                    recurrence: shift.recurrence ? shift.recurrence : null,
                                    flexibleHours: shift.flexibleHours ? shift.flexibleHours : null,
                                    flexibleTimePeriod: shift.flexibleTimePeriod ? shift.flexibleTimePeriod : null,
                                    volunteersNeeded: createOrUpdateRoleResponse.differentNumberOfVolunteersPerShift ? shift.volunteersNeeded : createOrUpdateRoleResponse.volunteersPerShift,
                                    volunteerCoordinatorID: shift.volunteerCoordinatorID ? shift.volunteerCoordinatorID : props.role.roleCoordinatorID,
                                    roleID: createOrUpdateRoleResponse.id,
                                    roleStringifiedSkills: createOrUpdateRoleResponse.stringifiedSkills,
                                    roleStringifiedCauses: opportunities.selectedOpportunity.stringifiedCauses ? opportunities.selectedOpportunity.stringifiedCauses : '',
                                    locationID: shift.locationID ? shift.locationID : '00000000-0000-0000-0000-000000000000',
                                    locationLongitude: locationLongitude,
                                    locationLatitude: locationLatitude,
                                    locationZipEstimate: shift.locationZipEstimate ? shift.locationZipEstimate : null,
                                    locationType: shift.locationType ? shift.locationType : null,
                                    monthlyRecurrenceWeek: shift.monthlyRecurrenceWeek,
                                    monthlyRecurrenceDay: shift.monthlyRecurrenceDay,
                                    weeklyRecurringDay: shift.weeklyRecurringDay
                                }
                            }
                        })

                        console.log('updated shift: ', updateShiftResponse.data.updateShift)

                        updatedShifts.push(updateShiftResponse.data.updateShift)
                    }
                })
            }

            if (props.deletedShifts && props.deletedShifts.length > 0) {
                await asyncForEach(props.deletedShifts, async (shift: Shift) => {
                    if (shift.id != '00000000-0000-0000-0000-000000000000') {
                        console.log('deleting shift: ', shift.id)
                        const deleteShiftResponse = await API.graphql({
                            query: deleteShift,
                            variables: {
                                input: {
                                    id: shift.id,
                                }
                            }
                        })
                    }
                })
            }

            const getRoleResponse: any = await API.graphql({
                query: getRole,
                variables: {
                    id: props.role.id
                }
            })

            const updatedRole = getRoleResponse.data.getRole

            console.log('updated role: ', updatedRole)

            toast.success('Role saved.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });

            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))

            return {
                updatedRole: updatedRole
            }

        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))
            console.log('ERROR updating opportunity roles/shifts.')
            console.log(err)
        }
    }
)

export const saveShift: any = createAsyncThunk(
    'opportunities/saveShift',
    async (props: { shift: Shift }, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Saving shift...'
            }))

            const { authAccount, opportunities }: any = getState()

            var updatedRoles = []

            console.log(authAccount.selectedStaffProfile)
            console.log(opportunities.selectedOpportunity)

            const updateShiftResponse: any = await API.graphql({
                query: updateShift,
                variables: {
                    input: {
                        id: props.shift.id,
                        type: props.shift.type,
                        live: props.shift.live,
                        name: props.shift.name,
                        summary: props.shift.summary,
                        startDate: props.shift.startDate,
                        startTime: props.shift.startTime,
                        endDate: props.shift.endDate,
                        endTime: props.shift.endTime,
                        timezone: props.shift.timezone,
                        timeFrame: props.shift.timeFrame,
                        timeFrameString: props.shift.timeFrameString,
                        recurrence: props.shift.recurrence,
                        weeklyRecurringDay: props.shift.weeklyRecurringDay,
                        monthlyRecurrenceWeek: props.shift.monthlyRecurrenceWeek,
                        monthlyRecurrenceDay: props.shift.monthlyRecurrenceDay,
                        flexibleHours: props.shift.flexibleHours,
                        flexibleTimePeriod: props.shift.flexibleTimePeriod,
                        volunteersNeeded: props.shift.volunteersNeeded,
                        volunteerCoordinatorID: props.shift.volunteerCoordinatorID,
                        roleID: props.shift.roleID,
                        roleStringifiedSkills: props.shift.roleStringifiedSkills,
                        roleStringifiedCauses: props.shift.roleStringifiedCauses,
                        locationID: props.shift.locationID ? props.shift.locationID : '00000000-0000-0000-0000-000000000000',
                        locationLongitude: props.shift.locationLongitude,
                        locationLatitude: props.shift.locationLatitude,
                        locationType: props.shift.locationType,
                        locationZipEstimate: props.shift.locationZipEstimate,
                        locationVariousDescription: props.shift.locationVariousDescription
                    }
                }
            })

            const updatedShift = updateShiftResponse.data.updateShift

            console.log('updated shift: ', updatedShift)

            toast.success('Shift saved.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });

            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))

            return {
                updatedShift: updatedShift
            }

        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))
            console.log('ERROR updating opportunity shift.')
            console.log(err)
        }
    }
)

export const updateOpportunityRoles: any = createAsyncThunk(
    'opportunities/updateOpportunityRoles',
    async (props: { roles: Role[], deletedRoles?: Role[], deletedShifts?: Shift[] }, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Updating your opportunity roles. This may take a few moments.'
            }))

            const { authAccount, opportunities }: any = getState()

            var updatedRoles = []

            console.log(authAccount.selectedStaffProfile)
            console.log(opportunities.selectedOpportunity)

            await asyncForEach(props.roles, async (role: Role) => {

                var createOrUpdateRoleResponse: any = null

                console.log('passed role: ', role)

                if (role.id == '00000000-0000-0000-0000-000000000000') {
                    const createRoleResponse: any = await API.graphql({
                        query: createRole,
                        variables: {
                            input: {
                                opportunityID: opportunities.selectedOpportunity.id,
                                organizationID: authAccount.selectedStaffProfile.organizationID,
                                roleTitle: role.roleTitle,
                                roleCoordinatorID: role.roleCoordinatorID,
                                roleRequirements: role.roleRequirements,
                                ageRequirement: role.ageRequirement ? role.ageRequirement : null,
                                minorsRequireSupervision: role.minorsRequireSupervision ? role.minorsRequireSupervision : null,
                                stringifiedSkills: role.stringifiedSkills,
                                stringifiedSkillsJSON: role.stringifiedSkillsJSON,
                                stringifiedCertifications: role.stringifiedCertifications,
                                recurrence: role.recurrence,
                                weeklyRecurrenceDays: role.weeklyRecurrenceDays,
                                weeklyRecurrenceSameVolunteers: role.weeklyRecurrenceSameVolunteers,
                                volunteersPerShift: role.volunteersPerShift,
                                differentNumberOfVolunteersPerShift: role.differentNumberOfVolunteersPerShift,
                                volunteerCommitmentMonths: role.volunteerCommitmentMonths,
                                volunteerCommitmentCanRenew: role.volunteerCommitmentCanRenew
                            }
                        }
                    })

                    createOrUpdateRoleResponse = createRoleResponse.data.createRole
                    console.log('created role: ', createRoleResponse.data.createRole)
                    updatedRoles.push(createRoleResponse.data.createRole)

                } else {
                    const updateRoleResponse: any = await API.graphql({
                        query: updateRole,
                        variables: {
                            input: {
                                id: role.id,
                                opportunityID: opportunities.selectedOpportunity.id,
                                organizationID: authAccount.selectedStaffProfile.organizationID,
                                roleTitle: role.roleTitle,
                                roleCoordinatorID: role.roleCoordinatorID,
                                roleRequirements: role.roleRequirements,
                                ageRequirement: role.ageRequirement ? role.ageRequirement : null,
                                minorsRequireSupervision: role.minorsRequireSupervision ? role.minorsRequireSupervision : null,
                                stringifiedSkills: role.stringifiedSkills,
                                stringifiedSkillsJSON: role.stringifiedSkillsJSON,
                                stringifiedCertifications: role.stringifiedCertifications,
                                recurrence: role.recurrence,
                                weeklyRecurrenceDays: role.weeklyRecurrenceDays,
                                weeklyRecurrenceSameVolunteers: role.weeklyRecurrenceSameVolunteers,
                                volunteersPerShift: role.volunteersPerShift,
                                differentNumberOfVolunteersPerShift: role.differentNumberOfVolunteersPerShift,
                                volunteerCommitmentMonths: role.volunteerCommitmentMonths,
                                volunteerCommitmentCanRenew: role.volunteerCommitmentCanRenew
                            }
                        }
                    })

                    createOrUpdateRoleResponse = updateRoleResponse.data.updateRole
                    console.log('updated roles: ', updateRoleResponse.data.updateRole)
                    updatedRoles.push(updateRoleResponse.data.updateRole)
                }

                if (role.shifts && role.shifts.items && role.shifts.items.length > 0) {

                    var updatedShifts = []

                    await asyncForEach(role.shifts.items, async (shift: Shift) => {
                        if (shift.id == '00000000-0000-0000-0000-000000000000') {

                            var locationLongitude = null;
                            var locationLatitude = null;

                            if (shift.locationZipEstimate) {
                                const zipcodeLookup = zipcodes.lookup(shift.locationZipEstimate)
                                locationLongitude = zipcodeLookup ? zipcodeLookup.longitude : null
                                locationLatitude = zipcodeLookup ? zipcodeLookup.latitude : null
                            } else {
                                locationLongitude = shift.locationLongitude ? shift.locationLongitude : shift.location && shift.location.address && shift.location.address.longitude ? shift.location.address.longitude : null
                                locationLatitude = shift.locationLatitude ? shift.locationLatitude : shift.location && shift.location.address && shift.location.address.latitude ? shift.location.address.latitude : null
                            }

                            const weeklyRecurringMultipleDays: any = null

                            const timeFrameString = shift.type == 'fixed' ? shift.startTime ? await calculateTimeFrame(shift.startTime, shift.recurrence && shift.recurrence == 'weekly' && shift.weeklyRecurringDay && shift.weeklyRecurringDay != 'multiple' ? shift.weeklyRecurringDay : shift.recurrence && shift.recurrence == 'weekly' && shift.weeklyRecurringDay && shift.weeklyRecurringDay == 'multiple' ? createOrUpdateRoleResponse.weeklyRecurrenceDays : shift.recurrence && shift.recurrence == 'monthly' && shift.monthlyRecurrenceDay ? shift.monthlyRecurrenceDay : shift.startDate ? shift.startDate : '') : null : 'flexible'

                            const createShiftResponse: any = await API.graphql({
                                query: createShift,
                                variables: {
                                    input: {
                                        type: shift.type,
                                        name: shift.name ? shift.name : null,
                                        summary: shift.summary ? shift.summary : null,
                                        startDate: shift.startDate ? shift.startDate : null,
                                        startTime: shift.startTime ? shift.startTime : null,
                                        endDate: shift.endDate ? shift.endDate : null,
                                        endTime: shift.endTime ? shift.endTime : null,
                                        timeFrameString: timeFrameString,
                                        recurrence: shift.recurrence ? shift.recurrence : null,
                                        flexibleHours: shift.flexibleHours ? shift.flexibleHours : null,
                                        flexibleTimePeriod: shift.flexibleTimePeriod ? shift.flexibleTimePeriod : null,
                                        volunteersNeeded: createOrUpdateRoleResponse.differentNumberOfVolunteersPerShift ? shift.volunteersNeeded : createOrUpdateRoleResponse.volunteersPerShift,
                                        volunteerCoordinatorID: shift.volunteerCoordinatorID ? shift.volunteerCoordinatorID : role.roleCoordinatorID,
                                        roleID: createOrUpdateRoleResponse.id,
                                        roleStringifiedSkills: createOrUpdateRoleResponse.stringifiedSkills,
                                        roleStringifiedCauses: opportunities.selectedOpportunity.stringifiedCauses ? opportunities.selectedOpportunity.stringifiedCauses : '',
                                        locationID: shift.locationID ? shift.locationID : null,
                                        locationLongitude: locationLongitude,
                                        locationLatitude: locationLatitude,
                                        locationZipEstimate: shift.locationZipEstimate ? shift.locationZipEstimate : null,
                                        locationType: shift.locationType ? shift.locationType : null,
                                        monthlyRecurrenceWeek: shift.monthlyRecurrenceWeek,
                                        monthlyRecurrenceDay: shift.monthlyRecurrenceDay,
                                        weeklyRecurringDay: shift.weeklyRecurringDay
                                    }
                                }
                            })

                            console.log('created shift: ', createShiftResponse.data.createShift)
                            updatedShifts.push(createShiftResponse.data.createShift)
                        } else {

                            var locationLongitude = null;
                            var locationLatitude = null;

                            if (shift.locationZipEstimate) {
                                const zipcodeLookup = zipcodes.lookup(shift.locationZipEstimate)
                                locationLongitude = zipcodeLookup ? zipcodeLookup.longitude : null
                                locationLatitude = zipcodeLookup ? zipcodeLookup.latitude : null
                            } else {
                                locationLongitude = shift.locationLongitude ? shift.locationLongitude : shift.location && shift.location.address && shift.location.address.longitude ? shift.location.address.longitude : null
                                locationLatitude = shift.locationLatitude ? shift.locationLatitude : shift.location && shift.location.address && shift.location.address.latitude ? shift.location.address.latitude : null
                            }

                            const timeFrameString = shift.type == 'fixed' ? shift.startTime ? await calculateTimeFrame(shift.startTime, shift.recurrence && shift.recurrence == 'weekly' && shift.weeklyRecurringDay && shift.weeklyRecurringDay != 'multiple' ? shift.weeklyRecurringDay : shift.recurrence && shift.recurrence == 'weekly' && shift.weeklyRecurringDay && shift.weeklyRecurringDay == 'multiple' ? createOrUpdateRoleResponse.weeklyRecurrenceDays : shift.recurrence && shift.recurrence == 'monthly' && shift.monthlyRecurrenceDay ? shift.monthlyRecurrenceDay : shift.startDate ? shift.startDate : '') : null : 'flexible'

                            const updateShiftResponse: any = await API.graphql({
                                query: updateShift,
                                variables: {
                                    input: {
                                        id: shift.id,
                                        type: shift.type,
                                        name: shift.name ? shift.name : null,
                                        summary: shift.summary ? shift.summary : null,
                                        startDate: shift.startDate ? shift.startDate : null,
                                        startTime: shift.startTime ? shift.startTime : null,
                                        endDate: shift.endDate ? shift.endDate : null,
                                        endTime: shift.endTime ? shift.endTime : null,
                                        timeFrameString: timeFrameString,
                                        recurrence: shift.recurrence ? shift.recurrence : null,
                                        flexibleHours: shift.flexibleHours ? shift.flexibleHours : null,
                                        flexibleTimePeriod: shift.flexibleTimePeriod ? shift.flexibleTimePeriod : null,
                                        volunteersNeeded: createOrUpdateRoleResponse.differentNumberOfVolunteersPerShift ? shift.volunteersNeeded : createOrUpdateRoleResponse.volunteersPerShift,
                                        volunteerCoordinatorID: shift.volunteerCoordinatorID ? shift.volunteerCoordinatorID : role.roleCoordinatorID,
                                        roleID: createOrUpdateRoleResponse.id,
                                        roleStringifiedSkills: createOrUpdateRoleResponse.stringifiedSkills,
                                        roleStringifiedCauses: opportunities.selectedOpportunity.stringifiedCauses ? opportunities.selectedOpportunity.stringifiedCauses : '',
                                        locationID: shift.locationID ? shift.locationID : null,
                                        locationLongitude: locationLongitude,
                                        locationLatitude: locationLatitude,
                                        locationZipEstimate: shift.locationZipEstimate ? shift.locationZipEstimate : null,
                                        locationType: shift.locationType ? shift.locationType : null,
                                        monthlyRecurrenceWeek: shift.monthlyRecurrenceWeek,
                                        monthlyRecurrenceDay: shift.monthlyRecurrenceDay,
                                        weeklyRecurringDay: shift.weeklyRecurringDay
                                    }
                                }
                            })

                            console.log('updated shift: ', updateShiftResponse.data.updateShift)

                            updatedShifts.push(updateShiftResponse.data.updateShift)
                        }
                    })
                }
            })

            if (props.deletedRoles && props.deletedRoles.length > 0) {
                await asyncForEach(props.deletedRoles, async (role: Role) => {
                    if (role.id != '00000000-0000-0000-0000-000000000000') {
                        console.log('deleting role: ', role.id)
                        const deleteRoleResponse = await API.graphql({
                            query: deleteRole,
                            variables: {
                                input: {
                                    id: role.id,
                                }
                            }
                        })

                        if (role.shifts && role.shifts.items && role.shifts.items.length > 0) {
                            await asyncForEach(role.shifts.items, async (shift: Shift) => {
                                const deleteRoleShiftResponse = await API.graphql({
                                    query: deleteShift,
                                    variables: {
                                        input: {
                                            id: shift.id,
                                        }
                                    }
                                })
                            })
                        }
                    }
                })
            }

            if (props.deletedShifts && props.deletedShifts.length > 0) {
                await asyncForEach(props.deletedShifts, async (shift: Shift) => {
                    if (shift.id != '00000000-0000-0000-0000-000000000000') {
                        console.log('deleting shift: ', shift.id)
                        const deleteShiftResponse = await API.graphql({
                            query: deleteShift,
                            variables: {
                                input: {
                                    id: shift.id,
                                }
                            }
                        })
                    }
                })
            }

            const { data }: any = await API.graphql({
                query: getOpportunity,
                variables: {
                    id: opportunities.selectedOpportunity.id
                }
            })

            const getOpportunityRolesResponse: any = await API.graphql({
                query: listRolesByOpportunity,
                variables: {
                    opportunityID: data.getOpportunity.id
                }
            })

            console.log(data)

            const updatedOpportunity = data.getOpportunity
            const opportunityRoles = getOpportunityRolesResponse.data.listRolesByOpportunity.items

            toast.success('Opportunity updated.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });

            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))

            return {
                updatedOpportunity,
                opportunityRoles
            }

        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading'
            }))
            console.log('ERROR updating opportunity roles/shifts.')
            console.log(err)
        }
    }
)

export const updateOpportunityApplication: any = createAsyncThunk(
    'opportunities/updateOpportunityApplication',
    async (props: OpportunityApplication, { dispatch, getState }) => {
        console.log('opportunity application params: ', props)
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Adding your opportunity application...'
            }))

            const { opportunities }: any = getState()

            if (opportunities.selectedOpportunity && opportunities.selectedOpportunity.application && opportunities.selectedOpportunity.application.items.length == 1) {
                const updateApplicationResponse: any = await API.graphql({
                    query: updateApplication,
                    variables: {
                        input: {
                            id: opportunities.selectedOpportunity.application.items[0].id,
                            opportunityID: opportunities.selectedOpportunity.id,
                            roleID: null, // added in case we make role-specific applications in the future
                            screeningCallRequired: props.screeningCallRequired,
                            recruitmentDeadlineForVolunteers: props.recruitmentDeadlineForVolunteers ? new Date(props.recruitmentDeadlineForVolunteers) : null,
                            targetNumberOfVolunteerHours: props.targetNumberOfVolunteerHours ? props.targetNumberOfVolunteerHours : null,
                            minimumNumberOfVolunteersRequired: props.minimumNumberOfVolunteersRequired ? props.minimumNumberOfVolunteersRequired : null,
                        }
                    }
                })

                if (updateApplicationResponse.data.updateApplication) {
                    const { data }: any = await API.graphql({
                        query: getOpportunity,
                        variables: {
                            id: opportunities.selectedOpportunity.id
                        }
                    })

                    console.log(data)

                    const updatedOpportunity = data.getOpportunity

                    toast.success('Opportunity updated.', {
                        position: "bottom-right",
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        containerId: 'main'
                    });

                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading.'
                    }))

                    return updatedOpportunity
                } else {
                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading.'
                    }))
                    console.log('ERROR updating opportunity application.')
                    console.log(updateApplicationResponse)
                }
            } else {
                const createApplicationResponse: any = await API.graphql({
                    query: createApplication,
                    variables: {
                        input: {
                            opportunityID: opportunities.selectedOpportunity.id,
                            roleID: null, // added in case we make role-specific applications in the future
                            screeningCallRequired: props.screeningCallRequired,
                            recruitmentDeadlineForVolunteers: props.recruitmentDeadlineForVolunteers ? new Date(props.recruitmentDeadlineForVolunteers) : null,
                            targetNumberOfVolunteerHours: props.targetNumberOfVolunteerHours ? props.targetNumberOfVolunteerHours : null,
                            minimumNumberOfVolunteersRequired: props.minimumNumberOfVolunteersRequired ? props.minimumNumberOfVolunteersRequired : null,
                        }
                    }
                })

                if (createApplicationResponse.data.createApplication) {
                    const { data }: any = await API.graphql({
                        query: getOpportunity,
                        variables: {
                            id: opportunities.selectedOpportunity.id
                        }
                    })

                    console.log(data)

                    const updatedOpportunity = data.getOpportunity

                    toast.success('Opportunity updated.', {
                        position: "bottom-right",
                        autoClose: 4000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        containerId: 'main'
                    });

                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading.'
                    }))

                    return updatedOpportunity
                } else {
                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading.'
                    }))
                    console.log('ERROR adding opportunity application.')
                    console.log(createApplicationResponse)
                }
            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.log('ERROR adding opportunity application.')
            console.log(err)
        }
    }
)

export const updateBackendOpportunity: any = createAsyncThunk(
    'opportunities/updateBackendOpportunity',
    async (opportunity: IUpdateOpportunity, { dispatch, getState }) => {
        try {

            const { opportunities, authAccount }: any = getState()

            const { data }: any = await API.graphql({
                query: updateOpportunity,
                variables: {
                    input: {
                        id: opportunities.selectedOpportunity.id,
                        title: opportunity.title,
                        overview: opportunity.overview,
                        descriptionStringified: opportunity.descriptionRTE,
                        descriptionRTE: opportunity.descriptionRTE,
                        responsibilitiesStringified: opportunity.responsibilities,
                        responsibilitiesRTE: opportunity.responsibilitiesRTE,
                    }
                }
            })

            console.log(data)

            const updatedOpportunity = data.updateOpportunity

            toast.success('Opportunity updated.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });

            return updatedOpportunity
        } catch (err) {
            console.error(err)
        }
    }
)

export const updateHeaderImage: any = createAsyncThunk(
    'opportunities/updateHeaderImage',
    async (path: string, { dispatch, getState }) => {
        try {

            const { opportunities, authAccount }: any = getState()

            const { data }: any = await API.graphql({
                query: updateOpportunity,
                variables: {
                    input: {
                        id: opportunities.selectedOpportunity.id,
                        headerImage: path,
                    }
                }
            })

            const updatedOpportunity = data.updateOpportunity

            toast.success('Image uploaded.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });

            return updatedOpportunity
        } catch (err) {
            console.error(err)
        }
    }
)

export const updateOpportunitySkill: any = createAsyncThunk(
    'opportunities/updateOpportunitySkill',
    async (skills: string[], { dispatch, getState }) => {
        try {

            const { opportunities, authAccount }: any = getState()

            var stringifiedSkills = ''

            skills.map((skill: string) => {
                stringifiedSkills += skill + ' - '
            })

            const { data }: any = await API.graphql({
                query: updateOpportunity,
                variables: {
                    input: {
                        id: opportunities.selectedOpportunity.id,
                        stringifiedSkills: stringifiedSkills
                    }
                }
            })

            const updatedOpportunity = data.updateOpportunity

            toast.success('Skills updated.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });

            return updatedOpportunity
        } catch (err) {
            console.error(err)
        }
    }
)

export const updateOpportunityCause: any = createAsyncThunk(
    'opportunities/updateOpportunityCause',
    async (causes: string[], { dispatch, getState }) => {
        try {

            const { opportunities, authAccount }: any = getState()

            var stringifiedCauses = ''

            causes.map((cause: string) => {
                stringifiedCauses += cause + ' - '
            })

            const { data }: any = await API.graphql({
                query: updateOpportunity,
                variables: {
                    input: {
                        id: opportunities.selectedOpportunity.id,
                        stringifiedCauses: stringifiedCauses
                    }
                }
            })

            const updatedOpportunity = data.updateOpportunity

            toast.success('Causes updated.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });

            return updatedOpportunity
        } catch (err) {
            console.error(err)
        }
    }
)

export const addOpportunityShift: any = createAsyncThunk(
    'opportunities/addOpportunityShift',
    async (shift: any, { dispatch, getState }) => {
        try {

            const { opportunities, authAccount }: any = getState()

            var createShiftResponse: any;

            if (shift.selectedShiftType == 'flexible') {
                var createdTimeCommitment = null
                const timeCommitment: any = await API.graphql({
                    query: createTimeCommitment,
                    variables: {
                        input: {
                            amount: shift.amount,
                            units: shift.units,
                            period: shift.period
                        }
                    }
                })
                createdTimeCommitment = timeCommitment.data.createTimeCommitment

                createShiftResponse = await API.graphql({
                    query: createShift,
                    variables: {
                        input: {
                            name: shift.name ? shift.name : null,
                            summary: shift.summary ? shift.summary : null,
                            type: shift.selectedShiftType,
                            opportunityID: opportunities.selectedOpportunity.id,
                            opportunitySiteID: shift.opportunitySite.id
                        }
                    }
                })

            } else if (shift.selectedShiftType == 'oneTime') {
                createShiftResponse = await API.graphql({
                    query: createShift,
                    variables: {
                        input: {
                            name: shift.name ? shift.name : null,
                            summary: shift.summary ? shift.summary : null,
                            type: shift.selectedShiftType,
                            startTime: new Date(shift.startTime),
                            duration: shift.duration,
                            opportunityID: opportunities.selectedOpportunity.id,
                            opportunitySiteID: shift.opportunitySite.id
                        }
                    }
                })
            }

            if (createShiftResponse) {
                toast.success('Shift added.', {
                    position: "bottom-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    containerId: 'main'
                });

                return createShiftResponse.data.createShift
            }
        } catch (err) {
            console.error(err)
        }
    }
)

export const setSelectedOpportunity: any = createAsyncThunk(
    'opportunities/setSelectedOpportunity',
    async (opportunity: any, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Loading selected opportunity...'
            }))

            await dispatch(clearSelectedOpportunity())

            const { data }: any = await API.graphql({
                query: getOpportunity,
                variables: {
                    id: opportunity.id
                }
            })

            const retrievedOpportunity: Opportunity = data.getOpportunity

            console.log('selected opportunity: ', retrievedOpportunity)

            const getOpportunityRolesResponse: any = await API.graphql({
                query: `
                query ListRolesByOpportunity(
                    $opportunityID: ID
                    $sortDirection: ModelSortDirection
                    $filter: ModelRoleFilterInput
                    $limit: Int
                    $nextToken: String
                  ) {
                listRolesByOpportunity(
                    opportunityID: $opportunityID
                    sortDirection: $sortDirection
                    filter: $filter
                    limit: $limit
                    nextToken: $nextToken
                  ) {
                    items {
                        id
                        opportunityID
                        organizationID
                        roleTitle
                        roleCoordinatorID
                        recurrence
                        weeklyRecurrenceDays
                        weeklyRecurrenceSameVolunteers
                        volunteersPerShift
                        differentNumberOfVolunteersPerShift
                        volunteerCommitmentMonths
                        volunteerCommitmentCanRenew
                        roleRequirements
                        ageRequirement
                        minorsRequireSupervision
                        stringifiedSkills
                        stringifiedSkillsJSON
                        stringifiedCertifications
                        shifts {
                          items {
                            id
                            type
                            live
                            name
                            summary
                            startDate
                            startTime
                            endDate
                            endTime
                            timezone
                            timeFrame
                            timeFrameString
                            recurrence
                            weeklyRecurringDay
                            monthlyRecurrenceWeek
                            monthlyRecurrenceDay
                            flexibleHours
                            flexibleTimePeriod
                            volunteersNeeded
                            volunteerCoordinatorID
                            roleID
                            roleStringifiedSkills
                            roleStringifiedCauses
                            locationID
                            locationLongitude
                            locationLatitude
                            locationType
                            locationZipEstimate
                            locationVariousDescription
                            createdAt
                            updatedAt
                            location {
                              id
                              name
                              details
                              type
                              addressID
                              address {
                                id
                                createdAt
                                city
                                latitude
                                longitude
                                state
                                streetAddress
                                unit
                                updatedAt
                                zip
                                zipPlusFour
                              }
                              organizationID
                              updatedAt
                            }
                            volunteerCoordinator {
                              authAccountID
                              authAccount {
                                id
                                firstName
                                lastName
                                name
                                owner
                                lastSelectedStaffProfileID
                                primaryEmail
                                updatedAt
                                volunteerProfileID
                                createdAt
                                communicationsProfileID
                              }
                              id
                              name
                              createdAt
                              organizationID
                              owner
                              role
                              updatedAt
                            }
                          }
                          nextToken
                        }
                        createdAt
                        updatedAt
                        roleCoordinator {
                          id
                          authAccountID
                          organizationID
                          role
                          name
                          createdAt
                          updatedAt
                          authAccount {
                            id
                            volunteerProfileID
                            lastSelectedStaffProfileID
                            communicationsProfileID
                            name
                            firstName
                            lastName
                            primaryEmail
                            createdAt
                            updatedAt
                            owner
                          }
                          owner
                          organization {
                            id
                            name
                            description
                            website
                            phoneNumber
                            stripeConnectedAccountID
                            stripeConnectedAccountStatus
                            createdAt
                            updatedAt
                            owner
                          }
                        }
                        organization {
                          id
                          name
                          description
                          locationID
                          location {
                              id
                              type
                              name
                              addressID
                              address {
                                  id
                                  longitude
                                  latitude
                                  streetAddress
                                  city
                                  state
                                  zip
                              }
                          }
                          website
                          phoneNumber
                          stripeConnectedAccountID
                          stripeConnectedAccountStatus
                          createdAt
                          updatedAt
                          owner
                        }
                      }
                      nextToken
                    }
                  }
                `,
                variables: {
                    opportunityID: opportunity.id
                }
            })

            dispatch(push(`/admin/opportunities/manage?id=${opportunity.id}`))

            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))

            return {
                opportunity: retrievedOpportunity,
                roles: getOpportunityRolesResponse.data.listRolesByOpportunity.items
            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.error(err)
        }
    }
)

export const setSelectedOpportunityRole: any = createAsyncThunk(
    'opportunities/setSelectedOpportunityRole',
    async (roleID: string, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Loading selected role...'
            }))

            const { data }: any = await API.graphql({
                query: getRole,
                variables: {
                    id: roleID
                }
            })

            const retrievedRole: Role = data.getRole

            console.log('selected role: ', retrievedRole)

            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))

            return {
                role: retrievedRole
            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.error(err)
        }
    }
)

export const getSelectedOpportunityImage: any = createAsyncThunk(
    'opportunities/getSelectedOpportunityImage',
    async (opportunity: any, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Loading selected opportunity image...'
            }))

            const { opportunities }: any = getState()

            if (opportunities && opportunities.selectedOpportunity) {
                const signedURL = await Storage.get(`opportunities/${opportunities.selectedOpportunity.id}/headerImage`)

                dispatch(setLoading({
                    loading: false,
                    message: 'Loading.'
                }))

                return {
                    opportunityImageURI: signedURL
                }
            } else {
                return {
                    opportunityImageURI: ''
                }
            }
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.error(err)
        }
    }
)

export const publishOpportunity: any = createAsyncThunk(
    'opportunities/publishOpportunity',
    async (params: any, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Publishing your opportunity...'
            }))

            const { opportunities, authAccount }: any = getState()

            console.log('opportunities redux state: ', opportunities)

            const { data }: any = await API.graphql({
                query: updateOpportunity,
                variables: {
                    input: {
                        id: opportunities.selectedOpportunity.id,
                        status: 'active',
                    }
                }
            })

            console.log('publishing opportunity: ', data)

            const updatedOpportunity = data.updateOpportunity

            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))

            toast.success('Opportunity published.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });

            // dispatch(push('/admin/opportunities/live'))

            return updatedOpportunity
        } catch (err) {
            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))
            console.error(err)
        }
    }
)

export const opportunitiesSlice = createSlice({
    name: 'opportunities',
    initialState,
    reducers: {
        clearLocalOpportunitiesData: (state) => {
            state.live = null
            state.drafts = null
            state.archive = null
        },
        clearSelectedOpportunity: (state) => {
            state.selectedOpportunity = null
            state.selectedOpportunityAction = null
            state.selectedOpportunityImage = null
        },
        updateSelectedOpportunity: (state, action) => {
            state.selectedOpportunity = action.payload
        },
        setSelectedOpportunityRoles: (state, action) => {
            state.selectedOpportunityRoles = action.payload
        }
    },
    extraReducers: (builder) => {
        builder.addCase(createOpportunityDraft.fulfilled, (state, { payload }) => {
            state.selectedOpportunity = null
            state.selectedOpportunity = payload
            state.selectedOpportunityAction = SelectedOpportunityActionType.update
        })
        builder.addCase(updateBackendOpportunity.fulfilled, (state, { payload }) => {
            state.selectedOpportunity = payload
        })
        builder.addCase(updateOpportunityBasics.fulfilled, (state, { payload }) => {
            state.selectedOpportunity = payload
        })
        builder.addCase(updateOpportunityDetails.fulfilled, (state, { payload }) => {
            state.selectedOpportunity = payload
        })
        builder.addCase(createNewRole.fulfilled, (state, { payload }) => {
            if (payload.newRole) {
                state.selectedOpportunityRole = payload.newRole
                if (state.selectedOpportunityRoles) {
                    state.selectedOpportunityRoles.push(payload.newRole)
                } else {
                    state.selectedOpportunityRoles = [payload.newRole]
                }
            }
        })
        builder.addCase(removeRole.fulfilled, (state, { payload }) => {
            if (payload.removedRole) {
                if (state.selectedOpportunityRoles) {
                    state.selectedOpportunityRoles = state.selectedOpportunityRoles.filter((role: Role) => {
                        return role.id != payload.removedRole.id
                    })
                }
                state.selectedOpportunityRole = state.selectedOpportunityRoles ? state.selectedOpportunityRoles[0] : null
            }
        })
        builder.addCase(duplicateRole.fulfilled, (state, { payload }) => {
            if (payload.duplicatedRole) {
                state.selectedOpportunityRole = payload.duplicatedRole
                if (state.selectedOpportunityRoles) {
                    state.selectedOpportunityRoles.push(payload.duplicatedRole)
                } else {
                    state.selectedOpportunityRoles = [payload.duplicatedRole]
                }
            }
        })
        builder.addCase(saveRole.fulfilled, (state, { payload }) => {
            if (payload.updatedRole) {
                state.selectedOpportunityRole = payload.updatedRole
            }
        })
        builder.addCase(updateOpportunityRoles.fulfilled, (state, { payload }) => {
            state.selectedOpportunity = payload.updatedOpportunity
            state.selectedOpportunityRoles = payload.opportunityRoles
        })
        builder.addCase(updateOpportunityApplication.fulfilled, (state, { payload }) => {
            state.selectedOpportunity = payload
        })
        builder.addCase(publishOpportunity.fulfilled, (state, { payload }) => {
            state.selectedOpportunity = payload
        })
        builder.addCase(getDraftOpportunities.fulfilled, (state, { payload }) => {
            state.drafts = payload
        })
        builder.addCase(getLiveOpportunities.fulfilled, (state, { payload }) => {
            state.live = payload
        })
        builder.addCase(setSelectedOpportunity.fulfilled, (state, { payload }) => {
            state.selectedOpportunity = null
            state.selectedOpportunity = payload.opportunity
            state.selectedOpportunityRoles = payload.roles
        })
        builder.addCase(setSelectedOpportunityRole.fulfilled, (state, { payload }) => {
            if (payload.role) {
                state.selectedOpportunityRole = payload.role
            }
        })
        builder.addCase(getSelectedOpportunityImage.fulfilled, (state, { payload }) => {
            state.selectedOpportunityImage = payload.opportunityImageURI
        })
        builder.addCase(getOpportunityMatchRequests.pending, (state, { payload }) => {
            state.selectedOpportunityMatchRequests = null
        })
        builder.addCase(getOpportunityMatchRequests.fulfilled, (state, { payload }) => {
            state.selectedOpportunityMatchRequests = payload
        })
        builder.addCase(getOpportunityActiveVolunteers.pending, (state, { payload }) => {
            state.selectedOpportunityActiveVolunteers = null
        })
        builder.addCase(getOpportunityActiveVolunteers.fulfilled, (state, { payload }) => {
            state.selectedOpportunityActiveVolunteers = payload
        })
    }
})

export const { clearLocalOpportunitiesData, clearSelectedOpportunity, updateSelectedOpportunity, setSelectedOpportunityRoles } = opportunitiesSlice.actions
export default opportunitiesSlice.reducer

async function asyncForEach(array: any[], callback: any) {
    for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
    }
}

async function calculateTimeFrame(time: string, day: string) {

    // time frame includes day of the week and general time of day (morning, afternoon, or evening)
    // to determine general time of day, we need two pieces of information: AM or PM and Hour# (1-12)
    // IF AM                 -> MORNING
    // IF PM + Hour# < 5     -> AFTERNOON
    // IF PM + Hour# >= 5    -> EVENING

    if (day.includes(' - ')) {

        const _days = day.split(' - ')

        var timeFrameString = ''

        if (_days.length > 0) {
            console.log('multiple days detected')
            await asyncForEach(_days, (_day: string) => {
                console.log('iterated on day: ', _day)
                var timeOfDay: 'morning' | 'afternoon' | 'evening' | null = null
                var dayOfTheWeek: number | null = null

                var isAM: boolean = false
                var isPM: boolean = false

                const hourNumber: Number = Number(time.split(':')[0])
                isPM = hourNumber >= 12
                isAM = hourNumber >= 0 && hourNumber < 12

                if (_day == 'sunday') dayOfTheWeek = 0
                else if (_day == 'monday') dayOfTheWeek = 1
                else if (_day == 'tuesday') dayOfTheWeek = 2
                else if (_day == 'wednesday') dayOfTheWeek = 3
                else if (_day == 'thursday') dayOfTheWeek = 4
                else if (_day == 'friday') dayOfTheWeek = 5
                else if (_day == 'saturday') dayOfTheWeek = 6
                else if (_day == '') dayOfTheWeek = -1
                else dayOfTheWeek = new Date(_day).getUTCDay()

                console.log(dayOfTheWeek, isAM, isPM, hourNumber, new Date(day))

                if (dayOfTheWeek == 0) {
                    if (isAM) timeFrameString += 'sundayMorning - '
                    else if (isPM && hourNumber < 5) timeFrameString += 'sundayAfternoon - '
                    else timeFrameString += 'sundayEvening - '
                } else if (dayOfTheWeek == 1) {
                    if (isAM) timeFrameString += 'mondayMorning - '
                    else if (isPM && hourNumber < 5) timeFrameString += 'mondayAfternoon - '
                    else timeFrameString += 'mondayEvening - '
                } else if (dayOfTheWeek == 2) {
                    if (isAM) timeFrameString += 'tuesdayMorning - '
                    else if (isPM && hourNumber < 5) timeFrameString += 'tuesdayAfternoon - '
                    else timeFrameString += 'tuesdayEvening - '
                } else if (dayOfTheWeek == 3) {
                    if (isAM) timeFrameString += 'wednesdayMorning - '
                    else if (isPM && hourNumber < 5) timeFrameString += 'wednesdayAfternoon - '
                    else timeFrameString += 'wednesdayEvening - '
                } else if (dayOfTheWeek == 4) {
                    if (isAM) timeFrameString += 'thursdayMorning - '
                    else if (isPM && hourNumber < 5) timeFrameString += 'thursdayAfternoon - '
                    else return timeFrameString += 'thursdayEvening - '
                } else if (dayOfTheWeek == 5) {
                    if (isAM) timeFrameString += 'fridayMorning - '
                    else if (isPM && hourNumber < 5) timeFrameString += 'fridayAfternoon - '
                    else return timeFrameString += 'fridayEvening - '
                } else if (dayOfTheWeek == 6) {
                    if (isAM) timeFrameString += 'saturdayMorning - '
                    else if (isPM && hourNumber < 5) timeFrameString += 'saturdayAfternoon - '
                    else timeFrameString += 'saturdayEvening - '
                }
            })
            console.log('timeframe string: ', timeFrameString)
            return timeFrameString
        }
    } else {
        var dayOfTheWeek: number | null = null
        var timeOfDay: 'morning' | 'afternoon' | 'evening' | null = null

        var isAM: boolean = false
        var isPM: boolean = false

        const hourNumber: Number = Number(time.split(':')[0])
        isPM = hourNumber >= 12
        isAM = hourNumber >= 0 && hourNumber < 12

        if (day == 'sunday') dayOfTheWeek = 0
        else if (day == 'monday') dayOfTheWeek = 1
        else if (day == 'tuesday') dayOfTheWeek = 2
        else if (day == 'wednesday') dayOfTheWeek = 3
        else if (day == 'thursday') dayOfTheWeek = 4
        else if (day == 'friday') dayOfTheWeek = 5
        else if (day == 'saturday') dayOfTheWeek = 6
        else dayOfTheWeek = new Date(day).getUTCDay()

        console.log(dayOfTheWeek, isAM, isPM, hourNumber, new Date(day))

        if (dayOfTheWeek == 0) {
            if (isAM) return 'sundayMorning'
            else if (isPM && hourNumber < 5) return 'sundayAfternoon'
            else return 'sundayEvening'
        } else if (dayOfTheWeek == 1) {
            if (isAM) return 'mondayMorning'
            else if (isPM && hourNumber < 5) return 'mondayAfternoon'
            else return 'mondayEvening'
        } else if (dayOfTheWeek == 2) {
            if (isAM) return 'tuesdayMorning'
            else if (isPM && hourNumber < 5) return 'tuesdayAfternoon'
            else return 'tuesdayEvening'
        } else if (dayOfTheWeek == 3) {
            if (isAM) return 'wednesdayMorning'
            else if (isPM && hourNumber < 5) return 'wednesdayAfternoon'
            else return 'wednesdayEvening'
        } else if (dayOfTheWeek == 4) {
            if (isAM) return 'thursdayMorning'
            else if (isPM && hourNumber < 5) return 'thursdayAfternoon'
            else return 'thursdayEvening'
        } else if (dayOfTheWeek == 5) {
            if (isAM) return 'fridayMorning'
            else if (isPM && hourNumber < 5) return 'fridayAfternoon'
            else return 'fridayEvening'
        } else if (dayOfTheWeek == 6) {
            if (isAM) return 'saturdayMorning'
            else if (isPM && hourNumber < 5) return 'saturdayAfternoon'
            else return 'saturdayEvening'
        }
    }
}